import {
  PRESENTATION_CHART_TYPES,
  PRESENTATION_SLIDE_KINDS,
  resolveExportImage,
} from '@utilities/powerpoint';

const CHART_TYPES = {
  'Bar Chart': PRESENTATION_CHART_TYPES.COLUMN,
  'Column Spline Chart': PRESENTATION_CHART_TYPES.COMBO,
  'Line Chart': PRESENTATION_CHART_TYPES.LINE,
  'Scatter Chart': PRESENTATION_CHART_TYPES.SCATTER,
};

const requiredTitle = (item, fallback) => item.title || fallback || 'Report detail';

// Canonical validation runs before the shared PowerPoint chart renderer. Normalize the Report IR
// here so Report data that uses Highcharts point shapes or numeric strings reaches that renderer.
const toFiniteChartValue = (value) => {
  if (Array.isArray(value)) {
    for (let index = value.length - 1; index >= 0; index -= 1) {
      const resolved = toFiniteChartValue(value[index]);
      if (resolved !== null) return resolved;
    }
    return null;
  }

  const resolved = value && typeof value === 'object' ? (value.y ?? value.value) : value;
  if (typeof resolved === 'number') return Number.isFinite(resolved) ? resolved : null;
  if (typeof resolved === 'string' && resolved.trim()) {
    const numericValue = Number(resolved);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
  return null;
};

const normalizeChartValues = (values = []) =>
  values.map((value) => (value === 'NAN_SENTINEL' ? null : toFiniteChartValue(value)));

// Tornado slides model gains, losses, and net change as required numeric series. A missing value
// represents no contribution, so use the neutral chart value instead of the nullable chart value.
const normalizeTornadoValues = (values) => normalizeChartValues(values).map((value) => value ?? 0);

const toChartSlide = (item, fallbackTitle) => ({
  categories: item.xAxis?.categories || [],
  chartType:
    item.type === 'Bar Chart' && item.barDir === 'bar'
      ? PRESENTATION_CHART_TYPES.BAR
      : CHART_TYPES[item.type],
  kind: PRESENTATION_SLIDE_KINDS.CHART,
  series: (item.series || []).map((series) => ({
    name: series.name,
    values: item.type === 'Scatter Chart' ? series.data : normalizeChartValues(series.data || []),
  })),
  title: requiredTitle(item, fallbackTitle),
  ...(item.type === 'Scatter Chart'
    ? {
        showRegressionLine: item.showRegressionLine,
        xAxisTitle: item.xAxisTitle,
        xMax: item.xMax,
        xMin: item.xMin,
        xValueFormat: item.xFormat || item.format,
        yAxisTitle: item.yAxisTitle,
        yMax: item.yMax,
        yMin: item.yMin,
      }
    : {}),
  ...(item.type === 'Column Spline Chart'
    ? { valueFormats: item.format }
    : { valueFormat: item.format }),
});

const toTableSlide = (item, fallbackTitle) => ({
  columns: (item.table?.columns || []).map((column, index) => ({
    format: column.format,
    header: column.Header || column.header || `Column ${index + 1}`,
    key: column.accessor || column.accessorKey,
  })),
  kind: PRESENTATION_SLIDE_KINDS.TABLE,
  rows: item.table?.data || [],
  title: requiredTitle(item, fallbackTitle),
});

const toTreeSlide = (item, fallbackTitle) => ({
  connectors: item.connectors || [],
  kind: PRESENTATION_SLIDE_KINDS.TREE,
  nodes: (item.nodes || []).map(({ nodeId, ...node }) => ({ ...node, id: nodeId })),
  title: requiredTitle(item, fallbackTitle),
});

const toImageSlide = async (item, fallbackTitle, resolveImage) => {
  const resolved = item.imageData
    ? { dataUrl: item.imageData, options: item.imageOptions }
    : await resolveImage(item.chart);
  if (!resolved?.dataUrl || !resolved?.options) {
    throw new TypeError(`Unable to resolve Report image: ${requiredTitle(item, fallbackTitle)}`);
  }
  return {
    data: resolved.dataUrl,
    kind: PRESENTATION_SLIDE_KINDS.IMAGE,
    placement: resolved.options,
    title: requiredTitle(item, fallbackTitle),
  };
};

const toSlide = async ({ fallbackTitle, item, resolveImage }) => {
  if (CHART_TYPES[item.type]) return toChartSlide(item, fallbackTitle);
  if (item.type === 'table') return toTableSlide(item, fallbackTitle);
  if (item.type === 'tree') return toTreeSlide(item, fallbackTitle);
  if (item.type === 'image') return toImageSlide(item, fallbackTitle, resolveImage);
  if (item.type === 'waterfall') {
    return {
      categories: item.categories,
      kind: PRESENTATION_SLIDE_KINDS.WATERFALL,
      labels: item.labels,
      series: item.series,
      title: requiredTitle(item, fallbackTitle),
      valueFormat: item.format,
    };
  }
  if (item.type === 'Tornado Chart') {
    return {
      categories: item.xAxis?.categories || [],
      kind: PRESENTATION_SLIDE_KINDS.TORNADO,
      series: (item.series || []).map(({ data, name }) => ({
        name,
        values: normalizeTornadoValues(data || []),
      })),
      title: requiredTitle(item, fallbackTitle),
      valueFormat: item.format,
    };
  }
  if (item.type === 'cards') {
    return {
      cards: item.cards,
      columns: item.columns,
      fitOnSingleSlide: item.fitOnSingleSlide,
      kind: PRESENTATION_SLIDE_KINDS.CARDS,
      title: requiredTitle(item, fallbackTitle),
    };
  }
  if (item.type === 'text') {
    return {
      body: item.body,
      kind: PRESENTATION_SLIDE_KINDS.TEXT,
      title: requiredTitle(item, fallbackTitle),
    };
  }
  if (item.type === 'takeaways') {
    return {
      body: item.body,
      kind: PRESENTATION_SLIDE_KINDS.TAKEAWAYS,
      title: requiredTitle(item, fallbackTitle),
    };
  }
  throw new TypeError(`Unsupported Report PowerPoint item type: ${item.type}`);
};

export const buildReportPresentation = async ({
  exportData,
  fileName,
  resolveImage = resolveExportImage,
}) => {
  const sections = [];
  for (const tab of exportData.tabsForExport || []) {
    const tabItems = (exportData.items || [])
      .filter((item) => item.tabId === tab.tabId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const slides = [];
    for (const item of tabItems) {
      // Image resolution is asynchronous and slide order must remain deterministic.
      // eslint-disable-next-line no-await-in-loop
      slides.push(await toSlide({ fallbackTitle: tab.tabName, item, resolveImage }));
    }
    sections.push({ id: String(tab.tabId), slides, title: tab.tabName });
  }

  return {
    fileName,
    footer: {
      attributes: exportData.shopperAttributes,
      dateRange: exportData.reportDateRange?.every(Boolean)
        ? exportData.reportDateRange
        : undefined,
      sourceLabel: exportData.reportType,
      sourceUrl: exportData.reportUrl,
    },
    sections,
    title: {
      heading: exportData.reportName || 'Report',
      subtitle: exportData.reportType || 'Report',
    },
  };
};
